import {
  observable, action, computed, autorun, makeObservable, runInAction, reaction,
  IMapDidChange,
  IArrayDidChange,
  IObjectDidChange,
} from 'mobx';
import { deepObserve } from 'mobx-utils';
import { isUuid, isReference } from '@mfe-4cs/util/validate';
import { assert } from '@mfe-4cs/util/assert';
import { Identity, ConsentGroup } from '@mfe-4cs/types/comsApi';
import { ApiLoadConsentResponse, PageDetails, TefComs } from '@mfe-4cs/types/typings';
import Service from '@mfe-4cs/util/Service';
import SessionStorage from '@mfe-4cs/util/SessionStorage';
import Tracker from '@mfe-4cs/classes/Tracker';
import { CustomerAttribute, CustomerAttributeSave } from '@mfec/customerAttributes/types';
import { customerAttributesDiffer } from '@mfec/customerAttributes/customerAttributesDiffer';
import { mapCustomerAttributeTypeToSaveType } from '@mfec/customerAttributes/mapCustomerAttributeTypeToSaveType';
import { consentAndCustomerAttributeChanges } from '@mfe-4cs/util/changeDetection';
import ClientStore from './ClientStore';
import { ConsentGroupStore } from './ConsentGroupStore';
import OcclStore from './OcclStore';
import { ConsentStore } from './ConsentStore';
import { HistoryStore } from './HistoryStore';

type ObservableChange = IObjectDidChange | IArrayDidChange | IMapDidChange;

export interface ComsStorePersist {
  identity: Identity;
  config: TefComs.ConfigOptions;
  page: ApiLoadConsentResponse;
}

export class ComsStore {
  public initialConsents: TefComs.ConsentStatus[] = [];
  public consentGroups?: ConsentGroupStore[] = undefined;
  public customerAttributes?: CustomerAttribute[] = [];
  public customerAttributesOrigin?: CustomerAttribute[] = [];
  public consentChangeHistory?: ComsStorePersist[] = [];
  public details?: PageDetails = undefined;
  public identity?: Identity = undefined;

  public config?: TefComs.ConfigOptions = undefined;
  public consentWhitelist: string[] | undefined = undefined;
  public client?: ClientStore;
  public history?: HistoryStore;
  public occl?: OcclStore;
  public fromStorage = false;

  public static readonly STORAGE_NAME = 'tef-coms4cs-state';

  public references: TefComs.Reference[] = [];
  public showReferences = false;
  public showVersions = false;
  public isExistingRegister = false;
  public isNewCustomer = false;

  public service = new Service();
  public tracker = new Tracker();

  public error?: Error = undefined;

  private lastConsentAllDefinedEventMessage = false;

  constructor() {
    makeObservable(this, {
      consentGroups: observable,
      customerAttributes: observable,
      setCustomerAttribute: action,
      identity: observable,
      error: observable,
      references: observable,
      fatal: computed,
      init: action,
      isDataLoaded: computed,
      isOcclReady: computed,
      hasModifiedConsents: computed,
    });
  }

  reset() {
    runInAction(() => {
      this.config = undefined;
      this.client = undefined;
      this.history = undefined;
      this.occl = undefined;
      this.fromStorage = false;
      this.showReferences = false;
      this.consentGroups = undefined;
      this.initialConsents = [];
      this.customerAttributes = [];
      this.customerAttributesOrigin = [];
      this.identity = undefined;
      this.error = undefined;
      this.references = [];
      this.showVersions = false;
      this.isExistingRegister = false;
      this.isNewCustomer = false;
    });
  }

  /** indicating a fatal error condition when starting up <tef-coms4cs> */
  public get fatal(): Error | undefined {
    return this.error
      ?? this.occl?.error
      ?? (this.isDataLoaded ? undefined : this.client?.error);
  }

  public async init(config: TefComs.ConfigOptions): Promise<void> {
    this.error = undefined; // reset error state -> important for browser tests

    this.config = {
      ...config,
      apiUri: config.apiUri?.replace(/\/$/, ''),
      occlUri: config.occlUri?.replace(/\/$/, ''),
      channel: config.channel || 'Shop',
      usertype: config.usertype || 'Agent',
      consentwhitelist: config.consentwhitelist?.trim(),
      references: config.references,
      showReferences: config.showReferences || false,
      showVersions: config.showVersions || false,
      isExistingRegister: config.isExistingRegister || false,
      isNewCustomer: config.isNewCustomer || false,
      registerId: config.registerId,
    };

    const once = autorun(() => {
      if (this.isDataLoaded && this.isOcclReady) {
        this.service.fireEvent<TefComs.EventDetails>({
          name: 'load/READY',
          level: 'info',
          message: 'micro-frontend is ready',
        });
        once();
      }
      if (this.fatal) {
        this.service.fireEvent<TefComs.EventDetails>({
          name: 'load/ERROR',
          level: 'error',
          error: this.fatal,
        });
        this.tracker.comsError(this.fatal.message);
        once();
      }
    });

    if (this.config.references) {
      try {
        this.references = JSON.parse(this.config.references) as TefComs.Reference[];
        this.references.forEach((ref) => {
          if (!isReference(ref)) {
            const refError = new Error();
            refError.message = 'invalid object structure';
            throw refError;
          }
        });
      } catch (error) {
        const errorObject = error as Error;
        const formatError = new Error(`attribute "references" invalid format: ${errorObject.message}`);
        this.error = formatError;
        throw formatError;
      }
    }

    // validate consentwhitelist
    if (this.config.consentwhitelist) {
      try {
        this.consentWhitelist = JSON.parse(this.config.consentwhitelist) as string[];
      } catch (error) {
        const errorObject = error as Error;
        const formatError = new Error(`attribute "consentwhitelist" invalid JSON format: ${errorObject.message}`);
        this.error = formatError;
        throw formatError;
      }
    }

    try {
      assert(
        !this.consentWhitelist || Array.isArray(this.consentWhitelist),
        'attribute "consentwhitelist" is not an array',
      );
      assert(
        !this.consentWhitelist || this.consentWhitelist.reduce<boolean>((acc, s) => acc && isUuid(s), true),
        'attribute "consentwhitelist" contains invalid UUIDs',
      );
      assert(!!this.config.apiUri, 'attribute "apiuri" is not set');

      if (!this.config.agentToken) {
        assert(!!this.config.spId, 'attribute "spId" is not set');
        assert(!!this.config.lcId || !!this.config.registerId, 'attribute "lcId" or "registerId" is not set');
      } else {
        assert(!!this.config.agentToken, 'attribute "agenttoken" is not set');
      }

      assert(!!this.config.lifecycle, 'attribute "lifecycle" is not set');
      assert(!this.config.mode || this.config.mode === 'overview' || this.config.mode === 'buttons', 'attribute "mode" is wrong');
    } catch (error) {
      this.error = error as Error;
      throw error;
    }

    Tracker.init(this.config);

    this.client = new ClientStore(this.config);
    if (this.config.occlUri) {
      this.occl = new OcclStore(this.config.occlUri, this.config.styleContext);
    }
    this.history = new HistoryStore(this.client);

    const identity = await this.client.getIdentity();
    runInAction(() => {
      this.identity = identity;
    });

    await this.loadConsents();
    this.triggerConsentsAllDefinedEvent();

    runInAction(() => {
      this.initialConsents = this.consents.map((consent) => consent.toSaveConsent());
    });

    autorun(() => {
      if (this.isDataLoaded) {
        const snapshot: ComsStorePersist = this.toJSON();
        SessionStorage.setItem(ComsStore.STORAGE_NAME, snapshot);
      }
    });

    this.updateConsentChangeHistory(); // initialize
    reaction(() => this.consentChangeHistoryCounter, () => this.updateConsentChangeHistory());
  }

  handleConsentGroupChange(change : ObservableChange) {
    // @ts-expect-error ".name is fine"
    // A consent/feature has been granted/revoked (see change.newValue, change.oldValue)
    if (change.type === 'update' && change.name === 'granted') {
      this.triggerConsentsAllDefinedEvent();
      this.triggerConsentsStatusEvent();
    }
  }

  handleCustomerAttributeChange(change : ObservableChange) {
    // @ts-expect-error ".name is fine"
    // A customer attribute has been granted/revoked (see change.newValue, change.oldValue)
    if (change.type === 'update' && change.name === 'granted') {
      // No triggerConsentsAllDefinedEvent()!
      this.triggerConsentsStatusEvent();
    }
  }

  triggerConsentsStatusEvent() {
    let eventMessage = 'no_changes';

    const changes = consentAndCustomerAttributeChanges({
      consentsA: this.initialConsents,
      consentsB: this.consents.map((c) => c.toSaveConsent()),
      customerAttributesA: this.customerAttributesOrigin ? this.customerAttributesOrigin : [],
      customerAttributesB: this.customerAttributes ? this.customerAttributes : [],
    });

    if (changes.granted > 0) {
      eventMessage = 'additional_grants';
    } else if (changes.revoked > changes.granted) {
      eventMessage = 'only_revokes';
    }

    this.service.fireEvent<TefComs.EventDetails>({
      name: 'consents/STATUS',
      level: 'info',
      message: eventMessage,
    });
  }

  triggerConsentsAllDefinedEvent() {
    const allConsentsRevokedOrGranted = this.consents.every((consent) => consent.isRevokedOrGranted);

    if (this.lastConsentAllDefinedEventMessage !== allConsentsRevokedOrGranted) {
      this.lastConsentAllDefinedEventMessage = allConsentsRevokedOrGranted;
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'consents/ALLDEFINED',
        level: 'info',
        message: String(this.lastConsentAllDefinedEventMessage),
      });
    }
  }

  get isDataLoaded(): boolean {
    return !!this.consentGroups;
  }

  get isOcclReady(): boolean {
    return (this.occl) ? this.occl.ready : true;
  }

  setCustomerAttribute(customerAttribute: CustomerAttribute): void {
    const remainingCustomerAttributes = this.customerAttributes?.filter(
      (cattr) => cattr.id !== customerAttribute.id,
    ) ?? [];

    this.customerAttributes = [
      ...remainingCustomerAttributes,
      customerAttribute,
    ];
  }

  // NOTE: used to detect manual changes
  get consentChangeHistoryCounter(): number {
    let total = 0;
    this.consentGroups?.forEach((group) => {
      total += group.changes;
      group.consents.forEach((consent) => {
        total += consent.changes;
        consent.channels?.forEach((feature) => {
          total += feature.changes;
        });
        consent.dataTypes?.forEach((feature) => {
          total += feature.changes;
        });
        consent.productTypes?.forEach((feature) => {
          total += feature.changes;
        });
        consent.purposeTypes?.forEach((feature) => {
          total += feature.changes;
        });
      });
    });
    return total;
  }

  get hasModifiedConsents(): boolean {
    const consentsAreModified = !!this.consents.find((c) => c.modified) || !!this.consents.find((c) => c.modeconstraint === 'edit');
    const customerAttributesModified = customerAttributesDiffer(this.customerAttributes, this.customerAttributesOrigin);
    return consentsAreModified || customerAttributesModified;
  }

  /** Flat list of consents. */
  get consents(): ConsentStore[] {
    if (!this.consentGroups) return [];
    return this.consentGroups
      .reduce<ConsentStore[]>((acc, { consents }) => [...acc, ...consents], []);
  }

  public findGroupByName(groupName: string): ConsentGroupStore | undefined {
    return this.consentGroups?.find((group) => group.name === groupName);
  }

  public async save(): Promise<void> {
    const refs = this.references.filter((r) => !!r.id);

    const consentsData = this.consents.map((consent) => consent.toSaveConsent());
    const references = refs.length > 0 ? refs.map(({ state, ...ref }) => ref) : undefined;

    await this.client?.saveConsents(
      consentsData,
      references,
      this.customerAttributes,
    );

    this.tracker.changePermissions(this.consentGroups ?? []);

    this.consents.forEach((cons) => cons.setModified(false));
    this.initialConsents = this.consents.map((consent) => consent.toSaveConsent());

    SessionStorage.setItem(ComsStore.STORAGE_NAME);
  }

  public async undo(): Promise<void> {
    const length = this.consentChangeHistory?.length ?? 0;
    if (length > 1) {
      const snapshot = this.consentChangeHistory?.[length - 2];
      SessionStorage.setItem(ComsStore.STORAGE_NAME, snapshot);
      await this.loadConsents();

      this.consentChangeHistory = [];
      this.updateConsentChangeHistory(); // re-initialize
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'consents/CHANGE',
        level: 'info',
        message: 'user changed a consent',
      });

      this.triggerConsentsAllDefinedEvent();
    }
  }

  public updateConsentChangeHistory(): void {
    const length = this.consentChangeHistory?.length ?? 0;
    if (length > 1) {
      this.consentChangeHistory?.shift(); // NOTE: we only keep up to 2 items
    }
    this.consentChangeHistory?.push(this.toJSON());
  }

  // eslint-disable-next-line class-methods-use-this
  public cancel() {
    SessionStorage.setItem(ComsStore.STORAGE_NAME);
  }

  public getShortConsents(): TefComs.ShortConsent[] {
    return this.consents.map((consent) => consent.toShortConsent());
  }

  // This is used by EPOS to get the consents payload for PATCH /consent/sp/{spId}/register
  // That endpoint will return the necessary payload to render documents, eg. those that
  // the customer must sign in person in the store.
  public getConsentStatus(): TefComs.ConsentAndCustomerAttributeSaveType[] {
    let customerAttributeConsents : CustomerAttributeSave[] = [];
    if (this.customerAttributes) {
      customerAttributeConsents = this.customerAttributes.map((cattr) => mapCustomerAttributeTypeToSaveType(cattr));
    }
    const consents = this.consents.map((consent) => consent.toSaveConsent());
    return [...consents, ...customerAttributeConsents];
  }

  public async loadConsents() {
    if (!this.client) return;
    const consentsFromSession = this.$getConsentsFromSession();
    this.fromStorage = !!consentsFromSession;
    const { groups, details, customerAttributes } = consentsFromSession ?? await this.client.loadConsents();

    const isInitialLoad = !this.consentChangeHistory?.length;

    // filter consentGroups whith whitelist if consentwhitelist is set
    // and groups are not loaded from session
    let filteredGroups: ConsentGroup[];
    if (this.consentWhitelist && !this.fromStorage) {
      filteredGroups = groups
        .map((group) => ({
          ...group,
          consents: group.consents.filter((consent) => this.consentWhitelist?.includes(consent.id)),
        }))
        .filter((group) => group.consents.length > 0);
    }
    runInAction(() => {
      this.customerAttributes = customerAttributes;
      this.customerAttributesOrigin = structuredClone(customerAttributes);
      this.details = details;
      this.consentGroups = (filteredGroups ?? groups).map((group) => new ConsentGroupStore(group, this.fromStorage));
    });

    // OBSERVE all changes to consents in consentGroups -> consents -> features
    deepObserve(this.consentGroups, (change) => { this.handleConsentGroupChange(change); });
    deepObserve(this.customerAttributes, (change) => { this.handleCustomerAttributeChange(change); });

    if (consentsFromSession) {
      // If the client.loadConsents is called, it fires "consents/LOAD" on its own so we only need to do this
      // if we restored the data from the session storage
      if (isInitialLoad) {
        this.service.fireEvent<TefComs.EventDetails>({
          name: 'consents/LOAD',
          level: 'info',
          message: 'loading consents successful',
        });
      } else {
        // Trigger initial STATUS event for EPOS to react correctly
        this.triggerConsentsStatusEvent();
      }
    }

    if (this.consentGroups?.length === 0) {
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'consents/EMPTY',
        level: 'info',
        message: 'no consents to be rendered',
      });
    }
  }

  public toJSON(): ComsStorePersist {
    return {
      page: {
        groups: this.consentGroups?.map((cg) => cg.toJSON()) ?? [],
        details: this.details ?? {},
        customerAttributes: this.customerAttributes ?? [],
      },
      identity: this.identity as Identity,
      config: this.config as TefComs.ConfigOptions,
    };
  }

  private $getConsentsFromSession(): ApiLoadConsentResponse | undefined {
    try {
      const dataInStorage = SessionStorage.getItem<ComsStorePersist>(ComsStore.STORAGE_NAME);

      if (!dataInStorage) return undefined;
      const match = (): boolean => (
        dataInStorage.config.lifecycle === this.config?.lifecycle
        && dataInStorage.config.gatewayToken === this.config?.gatewayToken
        && dataInStorage.config.apiUri === this.config?.apiUri
        && dataInStorage.identity.username === this.identity?.username
        && dataInStorage.identity.lcId === this.identity?.lcId
        && dataInStorage.identity.spId === this.identity?.spId
        && dataInStorage.identity.registerId === this.identity?.registerId
        && dataInStorage.identity.readonly === this.identity?.readonly
        && dataInStorage.config.lcId === this.config?.lcId
        && dataInStorage.config.registerId === this.config?.registerId
        && dataInStorage.config.spId === this.config?.spId
      );
      return match() ? dataInStorage.page : undefined;
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (error) { // ignore error
      return undefined;
    }
  }
}

const comsStore = new ComsStore();

export default comsStore;
